import { Minus, Plus } from 'lucide-react'
import { useState } from 'react'
import { cn } from '../../lib/cn'
import { IconButton } from '../IconButton/IconButton'
import { Input } from '../Input/Input'
import s from './CountInput.module.css'

/**
 * CountInput — **un décompte** : « 2 » WC, « 3 » occupants (#258). Saisissable au clavier **et**
 * par `+` / `−` en une touche, parce que l'agent compte debout, la tablette dans une main.
 *
 * Deux `IconButton` autour d'un `Input` : aucune primitive, le navigateur porte déjà rôle, focus et
 * clavier (règle 3). La valeur est **contrôlée** ; la frappe en cours vit dans le champ, pour que
 * « 1 » tapé avant « 2 » ne soit pas réécrit sous les doigts.
 *
 * ⚠️ **Il ne descend pas sous zéro, et zéro reste une valeur** : « 0 occupant » dit une maison
 * vide, pas un oubli. C'est l'appelant qui décide qu'une case de matrice ramenée à zéro disparaît.
 */
export interface CountInputProps {
  /** Nom accessible du champ, repris par les deux boutons (« Ajouter un — WC au RDC »). */
  label: string
  /** L'identifiant du champ, pour qu'un `Field` y rattache son libellé visible. */
  id?: string
  value: number | null
  onValueChange?: (value: number | null) => void
  /** Fiche transmise : tout se lit, rien ne bouge. */
  disabled?: boolean
  className?: string
}

export function CountInput({
  label,
  id,
  value,
  onValueChange,
  disabled,
  className,
}: CountInputProps) {
  const [typed, setTyped] = useState(textOf(value))
  const [held, setHeld] = useState(value)

  // La valeur a changé hors du champ (un `+`, une autre saisie) : le champ la reprend.
  if (value !== held) {
    setHeld(value)
    setTyped(textOf(value))
  }

  const invalid = parseCount(typed) === undefined

  return (
    <div className={cn(s.root, className)}>
      <IconButton
        label={`Retirer un — ${label}`}
        disabled={disabled === true || value === null || value === 0}
        onClick={() => onValueChange?.(Math.max(0, (value ?? 0) - 1))}
      >
        <Minus size={18} aria-hidden />
      </IconButton>
      <Input
        id={id}
        aria-label={label}
        className={s.field}
        inputMode="numeric"
        value={typed}
        invalid={invalid}
        disabled={disabled}
        onValueChange={(raw) => {
          setTyped(raw)
          const parsed = parseCount(raw)
          if (parsed !== undefined) onValueChange?.(parsed)
        }}
      />
      <IconButton
        label={`Ajouter un — ${label}`}
        disabled={disabled}
        onClick={() => onValueChange?.((value ?? 0) + 1)}
      >
        <Plus size={18} aria-hidden />
      </IconButton>
    </div>
  )
}

const textOf = (value: number | null): string => (value === null ? '' : String(value))

/**
 * Ce que la frappe vaut : un entier positif ou nul, `null` pour une case vidée, `undefined` quand
 * elle n'est **pas** retenue — l'espace du pavé numérique Android est toléré.
 */
function parseCount(raw: string): number | null | undefined {
  const cleaned = raw.replace(/\s/gu, '')
  if (cleaned === '') return null

  return /^\d+$/u.test(cleaned) ? Number(cleaned) : undefined
}
